import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { BulkEmailPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { NakedButton, Text } from '@frontend/design-system';
import StartBasicIcon from '../../assets/start-basic.png';
import StartBlankIcon from '../../assets/start-blank.png';
import StartMoreIcon from '../../assets/start-more.png';
import { useBulkMessageEditorContext } from '../../bulk-message-editor.context';
import { useBulkNavigator } from '../../hooks/use-bulk-navigator';
import { LESS_SEED_TEMPLATE_JSON, MORE_SEED_TEMPLATE_JSON } from './seed-templates';

type LaunchCard = {
  description: string;
  icon: string;
  onClick: () => void;
  title: string;
  trackingId: string;
};

export const LaunchCards = () => {
  const { t } = useTranslation('bulk-messaging');
  const { setTemplateJson } = useBulkMessageEditorContext();
  const navigateTo = useBulkNavigator(CampaignType.EMAIL);

  const cards: LaunchCard[] = [
    {
      title: t('Start Blank'),
      description: t('Create any email you can think of.'),
      icon: StartBlankIcon,
      onClick: () => navigateTo.composer({ startFresh: true }),
      trackingId: `${BulkEmailPrefixes.Editor}-compose-start-blank-btn`,
    },
    {
      title: t('Start With The Basics'),
      description: t('Good for new text-based emails.'),
      icon: StartBasicIcon,
      onClick: () => {
        setTemplateJson(LESS_SEED_TEMPLATE_JSON);
        navigateTo.composer({ startFresh: false });
      },
      trackingId: `${BulkEmailPrefixes.Editor}-compose-start-basics-btn`,
    },
    {
      title: t('Start With More'),
      description: t('Good for new promotional emails.'),
      icon: StartMoreIcon,
      onClick: () => {
        setTemplateJson(MORE_SEED_TEMPLATE_JSON);
        navigateTo.composer({ startFresh: false });
      },
      trackingId: `${BulkEmailPrefixes.Editor}-compose-start-more-btn`,
    },
  ];

  return (
    <div css={containerStyle}>
      {cards.map((card) => (
        <NakedButton key={card.title} css={cardStyle} onClick={card.onClick} trackingId={card.trackingId}>
          <img src={card.icon} alt={card.title} css={iconStyle} />
          <div>
            <Text css={{ fontWeight: theme.font.weight.bold }} size='large'>
              {card.title}
            </Text>
            <Text color='subdued' size='small'>
              {card.description}
            </Text>
          </div>
        </NakedButton>
      ))}
    </div>
  );
};

const containerStyle = {
  display: 'flex',
  gap: theme.spacing(2),
  flexWrap: 'wrap' as const,
  padding: theme.spacing(0, 3, 3),
  borderBottom: `1px solid ${theme.colors.neutral20}`,
  [`@media (max-width: ${breakpoints.xsmall.max}px)`]: {
    flexDirection: 'column' as const,
    alignItems: 'stretch' as const,
  },
};

const cardStyle = {
  display: 'flex',
  flexDirection: 'row' as const,
  alignItems: 'center',
  gap: theme.spacing(2),
  flex: '1 0 250px',
  padding: theme.spacing(1),
  backgroundColor: theme.colors.white,
  border: `1px solid ${theme.colors.neutral20}`,
  borderRadius: theme.borderRadius.medium,
  cursor: 'pointer',
  textAlign: 'left' as const,
  transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
  '&:hover': {
    boxShadow: theme.shadows.floating,
    borderColor: theme.colors.primary50,
  },
  [`@media (max-width: ${breakpoints.xsmall.max}px)`]: {
    flex: '1 1 auto',
    width: '100%',
  },
};

const iconStyle = {
  width: 64,
  height: 64,
  borderRadius: theme.borderRadius.medium,
  border: `1px solid ${theme.colors.neutral20}`,
};
